import React, { useState, cloneElement, useEffect, useCallback } from 'react';
import { TooltipTrigger as EncoreTooltipTrigger } from '@spotify-internal/encore-creator-web';
import { UnstyledButton } from '@mrkt/features/UnstyledButton';

type Props = Omit<
  React.ComponentProps<typeof EncoreTooltipTrigger>,
  'children' | 'overlay'
> & {
  /** Tooltip overlay */
  tooltip: React.ReactElement;
  /** Required for accessible tooltips */
  tooltipId: string;
  children: React.ReactElement;
};

export const TooltipTrigger = ({
  tooltipId,
  tooltip,
  children,
  ...props
}: Props) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [needsButtonWrapper, setNeedsButtonWrapper] = useState(false);

  const ref = useCallback((node: Element) => {
    /* If trigger element isn't already focusable (anchor or button), then it will need to be wrapped in an unstyled button for accessibility purposes */
    if (node !== null) {
      setNeedsButtonWrapper(node.tagName !== 'BUTTON' && node.tagName !== 'A');
    }
  }, []);

  const trigger = cloneElement(children, {
    ref: ref,
  });

  const getTrigger = () => {
    if (needsButtonWrapper) {
      return (
        <UnstyledButton aria-describedby={showTooltip ? tooltipId : undefined}>
          {trigger}
        </UnstyledButton>
      );
    }

    return cloneElement(trigger, {
      'aria-describedby': showTooltip ? tooltipId : undefined,
    });
  };

  useEffect(() => {
    /* if escape key is pressed, close tooltip */
    const onKeydown = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        setShowTooltip(false);
      }
    };

    /* add event listener for escape key press */
    window.addEventListener('keydown', onKeydown, true);

    /* clean up */
    return () => {
      window.removeEventListener('keydown', onKeydown, true);
    };
  });

  return (
    <EncoreTooltipTrigger
      overlay={
        showTooltip &&
        cloneElement(tooltip, {
          id: tooltipId,
        })
      }
      onShow={() => setShowTooltip(true)}
      onHide={() => setShowTooltip(false)}
      {...props}
    >
      {getTrigger()}
    </EncoreTooltipTrigger>
  );
};
